import { supabase } from '../lib/supabase';

export type StatusDocumento =
  | 'rascunho' | 'aberto' | 'em_tramitacao' | 'aguardando_solicitante'
  | 'aguardando_aceite' | 'aguardando_assinatura' | 'em_analise' | 'em_diligencia'
  | 'deferido' | 'indeferido' | 'arquivado' | 'cancelado' | 'concluido';

export interface SolicitacaoResumo {
  id: string;
  numero: string;
  hash_publico: string;
  titulo: string;
  status: StatusDocumento;
  modulo_codigo: string;
  data_abertura: string;
  data_limite: string | null;
  data_ultima_movimentacao: string;
  setor_atual?: { nome: string } | null;
  assunto?: { nome: string } | null;
}

export interface CriarSolicitacaoInput {
  estabelecimento_id: string;
  assunto_id: string;
  modulo_codigo: string;
  titulo: string;
  descricao: string;
  campos_customizados: Record<string, unknown>;
  setor_responsavel_id: string | null;
  latitude?: number | null;
  longitude?: number | null;
  endereco?: string | null;
  bairro?: string | null;
  cep?: string | null;
}

export interface EventoHistorico {
  id: string;
  tipo: string;
  titulo: string | null;
  mensagem: string | null;
  ator_nome: string;
  setor_origem_nome: string | null;
  setor_destino_nome: string | null;
  interno: boolean;
  criado_em: string;
}

export interface AnexoSolicitacao {
  id: string;
  nome_original: string;
  nome_storage: string;
  bucket: string;
  mime_type: string;
  tamanho_bytes: number;
  categoria: string | null;
  criado_em: string;
}

export const solicitacoesService = {
  async listarMinhas(cidadaoId: string): Promise<SolicitacaoResumo[]> {
    const { data, error } = await supabase
      .from('documentos')
      .select(`
        id, numero, hash_publico, titulo, status, modulo_codigo,
        data_abertura, data_limite, data_ultima_movimentacao,
        setor_atual:setores!setor_atual_id(nome),
        assunto:assuntos(nome)
      `)
      .eq('solicitante_cidadao_id', cidadaoId)
      .is('deletado_em', null)
      .order('data_ultima_movimentacao', { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as SolicitacaoResumo[];
  },

  async buscarPorNumero(cidadaoId: string, numero: string): Promise<(SolicitacaoResumo & { estabelecimento_id: string; descricao: string | null }) | null> {
    const { data, error } = await supabase
      .from('documentos')
      .select(`
        id, numero, hash_publico, titulo, status, modulo_codigo,
        data_abertura, data_limite, data_ultima_movimentacao, descricao,
        prioridade, setor_atual_id, campos_customizados, estabelecimento_id,
        setor_atual:setores!setor_atual_id(nome),
        assunto:assuntos(nome, descricao)
      `)
      .eq('solicitante_cidadao_id', cidadaoId)
      .eq('numero', numero)
      .is('deletado_em', null)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as (SolicitacaoResumo & { estabelecimento_id: string; descricao: string | null }) | null;
  },

  async criar(input: CriarSolicitacaoInput, cidadaoId: string): Promise<{ id: string; numero: string; hash_publico: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: input.estabelecimento_id,
        modulo_codigo: input.modulo_codigo,
        assunto_id: input.assunto_id,
        titulo: input.titulo,
        descricao: input.descricao,
        campos_customizados: input.campos_customizados,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: 'interna',
        solicitante_cidadao_id: cidadaoId,
        setor_atual_id: input.setor_responsavel_id,
        setor_origem_id: input.setor_responsavel_id,
        latitude: input.latitude ?? null,
        longitude: input.longitude ?? null,
        endereco: input.endereco ?? null,
        bairro: input.bairro ?? null,
        cep: input.cep ? input.cep.replace(/\D/g, '') : null,
      })
      .select('id, numero, hash_publico')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string; hash_publico: string };
  },

  async listarHistorico(documentoId: string): Promise<EventoHistorico[]> {
    const { data, error } = await supabase
      .from('documentos_historico')
      .select('id, tipo, titulo, mensagem, ator_nome, setor_origem_nome, setor_destino_nome, interno, criado_em')
      .eq('documento_id', documentoId)
      .eq('interno', false)
      .order('criado_em', { ascending: false });
    if (error) throw error;
    return (data ?? []) as EventoHistorico[];
  },

  async listarAnexos(documentoId: string): Promise<AnexoSolicitacao[]> {
    const { data, error } = await supabase
      .from('anexos')
      .select('id, nome_original, nome_storage, bucket, mime_type, tamanho_bytes, categoria, criado_em')
      .eq('documento_id', documentoId)
      .is('deletado_em', null)
      .order('criado_em', { ascending: false });
    if (error) throw error;
    return (data ?? []) as AnexoSolicitacao[];
  },

  async uploadAnexo(args: {
    documentoId: string;
    estabelecimentoId: string;
    cidadaoId: string;
    cidadaoNome: string;
    arquivo: File;
    categoria?: string;
  }): Promise<AnexoSolicitacao> {
    const { documentoId, estabelecimentoId, cidadaoId, cidadaoNome, arquivo, categoria } = args;
    const ext = arquivo.name.split('.').pop() ?? 'bin';
    const nomeStorage = `${estabelecimentoId}/${documentoId}/${crypto.randomUUID()}.${ext}`;

    const { error: erroUp } = await supabase.storage
      .from('documentos')
      .upload(nomeStorage, arquivo, { contentType: arquivo.type });
    if (erroUp) throw erroUp;

    const { data, error } = await supabase
      .from('anexos')
      .insert({
        estabelecimento_id: estabelecimentoId,
        documento_id: documentoId,
        nome_original: arquivo.name,
        nome_storage: nomeStorage,
        bucket: 'documentos',
        mime_type: arquivo.type,
        tamanho_bytes: arquivo.size,
        categoria: categoria ?? null,
        cidadao_id: cidadaoId,
        uploader_nome: cidadaoNome,
      })
      .select('id, nome_original, nome_storage, bucket, mime_type, tamanho_bytes, categoria, criado_em')
      .single();
    if (error) throw error;
    return data as AnexoSolicitacao;
  },

  async getAnexoUrl(anexoId: string): Promise<string> {
    const { data, error } = await supabase.rpc('cidadao_anexo_url', { p_anexo_id: anexoId });
    if (error) throw error;
    if (!data || (data as { erro?: string }).erro) {
      throw new Error((data as { erro?: string })?.erro ?? 'Anexo não disponível.');
    }
    const info = data as { bucket: string; nome_storage: string };
    const { data: signed, error: e2 } = await supabase.storage
      .from(info.bucket)
      .createSignedUrl(info.nome_storage, 60);
    if (e2) throw e2;
    return signed.signedUrl;
  },

  async responder(documentoId: string, mensagem: string): Promise<void> {
    const { data, error } = await supabase.rpc('cidadao_responder_protocolo', {
      p_documento_id: documentoId,
      p_mensagem: mensagem,
    });
    if (error) throw error;
    if (data && typeof data === 'object' && 'erro' in data) {
      throw new Error(String((data as { erro: string }).erro));
    }
  },
};

// ============================================================================
// Acompanhamento publico anonimo (por numero + hash)
// ============================================================================

export interface AcompanhamentoPublico {
  documento: {
    numero: string;
    titulo: string;
    descricao: string | null;
    status: string;
    prioridade: string;
    data_abertura: string;
    data_limite: string | null;
    data_conclusao: string | null;
    modulo_codigo: string;
  };
  estabelecimento: {
    nome: string;
    logo_url: string | null;
    cor_primaria: string | null;
  };
  assunto: {
    nome: string;
    descricao: string | null;
  };
  historico: Array<{
    tipo: string;
    titulo: string | null;
    mensagem: string | null;
    ator_nome: string;
    criado_em: string;
    setor_origem_nome: string | null;
    setor_destino_nome: string | null;
  }>;
  anexos: Array<{
    nome_original: string;
    tamanho_bytes: number;
    mime_type: string;
    criado_em: string;
  }>;
}

export const acompanhamentoPublicoService = {
  async buscar(numero: string, hash: string): Promise<AcompanhamentoPublico | { erro: string }> {
    const { data, error } = await supabase.rpc('protocolo_acompanhar_publico', {
      p_numero: numero,
      p_hash: hash,
    });
    if (error) throw error;
    return data as AcompanhamentoPublico | { erro: string };
  },
};
